// Datenschicht für die vom Admin verwalteten Bildergalerien.
//
// Struktur in der Realtime Database:
//
//   /gallery/collections/<schluessel>            = { caption, mainimage, columns, order, items }
//   /gallery/highlights                          = { columns, items }
//   /gallery/landing                             = { columns, items }
//   /gallery/neuheiten                           = { columns, items }
//
// Bei der Startseite steht das erste Bild gross oben, die uebrigen darunter
// nebeneinander. Die Reihenfolge entscheidet also, welches das grosse ist.
//
// columns = { web, tablet, mobile }: Bilder nebeneinander, je Geraeteklasse
// ein eigener Wert.
//
// items ist ein geordnetes Array. Jeder Eintrag hat ein src und, sofern das
// Bild über den Admin hochgeladen wurde, zusätzlich einen storagePath. Fehlt
// der storagePath, stammt das Bild aus public/assets und liegt im Repository.

import { ref, onValue, get, set } from 'firebase/database';
import { database } from './firebase';

// Die drei Kategorien, mit denen die Seite startet. Weitere legt der Admin an,
// diese drei lassen sich auch entfernen — massgeblich ist allein, was in der
// Datenbank steht. Die Liste dient der Erstbefuellung und als Rueckfallebene,
// solange die Datenbank nichts enthaelt.
export const COLLECTION_KEYS = ['earrings', 'chains', 'rings'];

export const SPALTEN_STANDARD = { web: 3, tablet: 3, mobile: 3 };

// Bilder nebeneinander, je Kategorie und Geraeteklasse im Admin einstellbar.
// Die Bereiche unterscheiden sich: am Rechner wirkt weniger als drei verloren,
// am Handy ist ueber drei nichts mehr zu erkennen, das Tablet liegt dazwischen.
// Welche Klasse gilt, entscheidet currentDevice in layout.js.
export const SPALTEN_BEREICH = {
  web: { min: 3, max: 5 },
  tablet: { min: 1, max: 4 },
  mobile: { min: 1, max: 3 },
};

function begrenze(raw, fallback, bereich) {
  const zahl = Math.round(Number(raw));
  if (!Number.isFinite(zahl)) {
    return fallback;
  }
  return Math.min(bereich.max, Math.max(bereich.min, zahl));
}

// Die erste Fassung hielt eine einzelne Zahl fuer beide Ansichten fest; solche
// Eintraege gelten hier fuer beide, bis der Admin sie neu setzt.
function normalizeSpalten(raw, fallback) {
  const quelle = typeof raw === 'number' ? { web: raw, tablet: raw, mobile: raw } : raw || {};
  return {
    web: begrenze(quelle.web, fallback.web, SPALTEN_BEREICH.web),
    // Eintraege von vor der Tablet-Unterscheidung haben kein tablet: dann gilt
    // der Standard, bis der Admin den Regler bewegt.
    tablet: begrenze(quelle.tablet, fallback.tablet, SPALTEN_BEREICH.tablet),
    mobile: begrenze(quelle.mobile, fallback.mobile, SPALTEN_BEREICH.mobile),
  };
}

function staticItems(directory, prefix, count) {
  return Array.from({ length: count }, (unused, index) => ({
    src: `./assets/images/collections/${directory}/${prefix}${index + 1}.webp`,
  }));
}

// Die Fotos in public/assets lagen früher als PNG vor, zehnmal so groß wie
// nötig. Datenbankeinträge aus dieser Zeit zeigen noch auf .png — sie werden
// beim Lesen umgeschrieben, statt die Datenbank migrieren zu müssen. Speichert
// der Admin eine Kategorie, landet die neue Adresse von selbst darin.
function optimizedSrc(src) {
  return typeof src === 'string' && src.startsWith('./assets/images/')
    ? src.replace(/\.png$/i, '.webp')
    : src;
}

// Der Bestand aus public/assets. Dient als Anzeige-Fallback, solange die
// Datenbank leer ist, und als Startbefüllung beim ersten Öffnen des Admins.
export const DEFAULT_GALLERY = {
  collections: {
    earrings: {
      caption: 'Ohrringe',
      mainimage: './assets/images/collections/earrings/earrings16.webp',
      columns: SPALTEN_STANDARD,
      order: 0,
      items: staticItems('earrings', 'earrings', 36),
    },
    chains: {
      caption: 'Ketten',
      mainimage: './assets/images/collections/chains/chain2.webp',
      columns: SPALTEN_STANDARD,
      order: 1,
      items: staticItems('chains', 'chain', 9),
    },
    rings: {
      caption: 'Ringe',
      mainimage: './assets/images/collections/rings/ring9.webp',
      columns: SPALTEN_STANDARD,
      order: 2,
      items: staticItems('rings', 'ring', 12),
    },
  },
  highlights: {
    // Highlights sind grossformatig gedacht: am Handy eines ueber die volle
    // Breite. Am Rechner sind drei das Minimum des erlaubten Bereichs.
    columns: { web: 3, tablet: 1, mobile: 1 },
    items: [
      { src: './assets/images/highlights/highlights1.webp' },
      { src: './assets/images/highlights/highlights2.webp' },
    ],
  },
  // Neuheiten starten leer: ohne Bild erscheint der Menuepunkt nicht.
  neuheiten: {
    columns: SPALTEN_STANDARD,
    items: [],
  },
  landing: {
    columns: SPALTEN_STANDARD,
    items: [
      // Das erste ist das grosse Bild oben.
      { src: './assets/images/landing/landing1.webp' },
      { src: './assets/images/landing/landing2.webp' },
      { src: './assets/images/landing/landing3.webp' },
      { src: './assets/images/landing/landing4.webp' },
    ],
  },
};

// Die Datenbank liefert Arrays mit Lücken als Objekt zurück, außerdem können
// einzelne Felder fehlen. Deshalb wird alles auf eine feste Form gebracht.
function normalizeItems(raw) {
  if (!raw) {
    return [];
  }
  const list = Array.isArray(raw) ? raw : Object.values(raw);
  return list
    .filter((item) => item && typeof item.src === 'string' && item.src.length > 0)
    .map((item) => ({
      src: optimizedSrc(item.src),
      storagePath: item.storagePath || null,
    }));
}

// Fuer Kategorien, die der Admin selbst angelegt hat und fuer die es im
// Repository nichts gibt.
const LEERE_KATEGORIE = {
  caption: 'Kategorie',
  mainimage: '',
  columns: SPALTEN_STANDARD,
  order: 100,
  items: [],
};

function normalizeCategory(raw, fallback) {
  const items = normalizeItems(raw && raw.items);
  const mainimage = optimizedSrc(raw && raw.mainimage);
  const order = Number(raw && raw.order);
  return {
    caption: (raw && raw.caption) || fallback.caption,
    columns: normalizeSpalten(raw && raw.columns, fallback.columns),
    order: Number.isFinite(order) ? order : fallback.order,
    // Zeigt das Titelbild auf ein inzwischen gelöschtes Bild, rückt das erste
    // Bild der Liste nach. Eine leere Kategorie bleibt leer — auf der Website
    // wird sie dann ausgeblendet, statt den Bestand aus dem Repository zu
    // zeigen.
    mainimage:
      mainimage && items.some((item) => item.src === mainimage)
        ? mainimage
        : (items[0] && items[0].src) || '',
    items,
  };
}

// Reihenfolge der Kategorien: nach order, bei Gleichstand nach Beschriftung.
export function collectionKeys(collections) {
  return Object.keys(collections).sort((a, b) => {
    const abstand = collections[a].order - collections[b].order;
    return abstand !== 0 ? abstand : collections[a].caption.localeCompare(collections[b].caption, 'de');
  });
}

// Leere Kategorien gehoeren nicht auf die Website.
export function visibleCollectionKeys(collections) {
  return collectionKeys(collections).filter((key) => collections[key].items.length > 0);
}

// Datenbankschluessel duerfen . $ # [ ] / nicht enthalten; uebrig bleiben hier
// ohnehin nur Kleinbuchstaben, Ziffern und Bindestriche.
function schluessel(text) {
  return text
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function createCollectionKey(caption, vorhandene) {
  const basis = schluessel(caption) || 'kategorie';
  let key = basis;
  let zaehler = 2;
  while (vorhandene.includes(key)) {
    key = `${basis}-${zaehler}`;
    zaehler += 1;
  }
  return key;
}

// Highlights und Startseite sind einfache Listen ohne Titelbild und
// Beschriftung — anders als die Kategorien der Schmuckstuecke.
function normalizeListe(raw, fallback) {
  const items = normalizeItems(raw && raw.items);
  return {
    columns: normalizeSpalten(raw && raw.columns, fallback.columns),
    items: items.length > 0 ? items : fallback.items,
  };
}

export function normalizeGallery(raw) {
  // Welche Kategorien es gibt, steht in der Datenbank — der Admin legt sie an
  // und entfernt sie. Nur solange dort nichts steht, gilt der Bestand aus dem
  // Repository.
  const gespeichert = raw && raw.collections;
  const quelle =
    gespeichert && Object.keys(gespeichert).length > 0 ? gespeichert : DEFAULT_GALLERY.collections;
  const collections = {};
  Object.keys(quelle).forEach((key) => {
    collections[key] = normalizeCategory(
      quelle[key],
      DEFAULT_GALLERY.collections[key] || LEERE_KATEGORIE
    );
  });
  return {
    collections,
    highlights: normalizeListe(raw && raw.highlights, DEFAULT_GALLERY.highlights),
    landing: normalizeListe(raw && raw.landing, DEFAULT_GALLERY.landing),
    neuheiten: normalizeListe(raw && raw.neuheiten, DEFAULT_GALLERY.neuheiten),
  };
}

// Liefert sofort den aktuellen Stand und danach jede Änderung. Der
// Rückgabewert hebt das Abo wieder auf.
// Der zuletzt gesehene Stand, damit beim naechsten Besuch sofort die
// richtigen Bilder stehen. Die Antwort der Datenbank braucht rund eine
// drittel Sekunde; vorher zeigte die Seite den Bestand aus dem Repository und
// tauschte ihn sichtbar aus.
const SPEICHER_SCHLUESSEL = 'augenstern-gallery';
// Gewartet wird nur so lange auf die Datenbank, bevor der Bestand aus dem
// Repository einspringt — fuer den Fall, dass gar keine Antwort kommt.
const FRIST_MS = 2500;

export function galleryAusSpeicher() {
  try {
    const roh = window.localStorage.getItem(SPEICHER_SCHLUESSEL);
    return roh ? JSON.parse(roh) : null;
  } catch (error) {
    // Privates Fenster, volle Ablage, abgeschaltete Speicherung: dann eben ohne.
    return null;
  }
}

export function galleryInSpeicher(roh) {
  try {
    window.localStorage.setItem(SPEICHER_SCHLUESSEL, JSON.stringify(roh));
  } catch (error) {
    // Siehe oben — ein fehlgeschlagenes Merken darf die Anzeige nicht stoeren.
  }
}

export function subscribeGallery(callback) {
  const zwischenstand = galleryAusSpeicher();
  let beantwortet = false;
  if (zwischenstand) {
    beantwortet = true;
    callback(normalizeGallery(zwischenstand));
  }
  const frist = setTimeout(() => {
    if (!beantwortet) {
      beantwortet = true;
      callback(normalizeGallery(null));
    }
  }, FRIST_MS);

  const abo = onValue(
    ref(database, 'gallery'),
    (snapshot) => {
      beantwortet = true;
      clearTimeout(frist);
      const roh = snapshot.val();
      galleryInSpeicher(roh);
      callback(normalizeGallery(roh));
    },
    () => {
      beantwortet = true;
      clearTimeout(frist);
      callback(normalizeGallery(zwischenstand));
    }
  );

  return () => {
    clearTimeout(frist);
    abo();
  };
}

export async function readGallery() {
  const snapshot = await get(ref(database, 'gallery'));
  return { exists: snapshot.exists(), gallery: normalizeGallery(snapshot.val()) };
}

export function writeCollection(key, category) {
  return set(ref(database, `gallery/collections/${key}`), {
    caption: category.caption,
    mainimage: category.mainimage,
    columns: category.columns,
    order: category.order,
    items: category.items,
  });
}

export function deleteCollection(key) {
  return set(ref(database, `gallery/collections/${key}`), null);
}

export function writeHighlights(category) {
  return set(ref(database, 'gallery/highlights'), {
    columns: category.columns,
    items: category.items,
  });
}

export function writeNeuheiten(category) {
  return set(ref(database, 'gallery/neuheiten'), {
    columns: category.columns,
    items: category.items,
  });
}

export function writeLanding(category) {
  return set(ref(database, 'gallery/landing'), {
    columns: category.columns,
    items: category.items,
  });
}

// Schreibt den Bestand aus public/assets in die Datenbank, damit der Admin
// die vorhandenen Bilder sortieren und entfernen kann, ohne sie erneut
// hochladen zu müssen.
export async function seedGallery() {
  await Promise.all([
    ...COLLECTION_KEYS.map((key) => writeCollection(key, DEFAULT_GALLERY.collections[key])),
    writeHighlights(DEFAULT_GALLERY.highlights),
    writeLanding(DEFAULT_GALLERY.landing),
  ]);
}
